import { Box, Chip, IconButton, Tooltip, Typography } from "@mui/material";
import { DataGrid, type GridColDef } from "@mui/x-data-grid";
import { type FirstTierSupplier } from "@/api/supplierService";
import { formattedDateTime } from "@/utils/date";
import SettingsIcon from "@mui/icons-material/Settings";
import DescriptionIcon from "@mui/icons-material/Description";
import { Link as RouterLink } from "react-router-dom";

interface FirstTierSupplierTableProps {
  data: FirstTierSupplier[];
  isLoading: boolean;
  canEdit: boolean;
  handleClickEdit: (supplier: FirstTierSupplier | null) => () => void;
  handleClickDetail: (
    supplierCode: string | null,
    templateType: string | null,
  ) => string | null;
}

const FirstTierSupplierTable = ({
  data,
  isLoading,
  canEdit,
  handleClickEdit,
  handleClickDetail,
}: FirstTierSupplierTableProps) => {
  const columns: GridColDef<FirstTierSupplier>[] = [
    {
      headerName: "Actions",
      headerAlign: "center",
      field: "actions",
      width: 120,
      sortable: false,
      headerClassName: "custom-header",
      renderCell: (param) => (
        <Box
          sx={{
            display: "flex",
            textAlign: "center",
            alignItems: "center",
            height: "100%",
            justifyContent: "center",
          }}
        >
          <Tooltip
            title={"ตั้งค่า"}
            placement="top"
            slotProps={{
              popper: {
                modifiers: [{ name: "offset", options: { offset: [0, -10] } }],
              },
            }}
          >
            <span>
              <IconButton
                onClick={handleClickEdit(param.row)}
                disabled={!canEdit}
              >
                <SettingsIcon color={canEdit ? "primary" : "disabled"} />
              </IconButton>
            </span>
          </Tooltip>
          <Tooltip
            title={"รายละเอียด"}
            placement="top"
            slotProps={{
              popper: {
                modifiers: [{ name: "offset", options: { offset: [0, -10] } }],
              },
            }}
          >
            <span>
              <IconButton
                component={RouterLink}
                to={handleClickDetail(
                  param.row.supplier_code?.toString() ?? null,
                  param.row.template_type ?? null,
                ) ?? ""}
                disabled={
                  !param?.row?.template_type ||
                  param?.row?.template_type === "None"
                }
              >
                <DescriptionIcon
                  color={
                    !param?.row?.template_type ||
                    param?.row?.template_type === "None"
                      ? "disabled"
                      : "primary"
                  }
                />
              </IconButton>
            </span>
          </Tooltip>
        </Box>
      ),
    },
    {
      field: "supplier_code",
      headerName: "S Code APro",
      width: 130,
      headerClassName: "custom-header",
      renderCell: (param) => (
        <Box sx={{ display: "flex", alignItems: "center", height: "100%" }}>
          {param.value}
        </Box>
      ),
    },
    {
      field: "supplier_abbr",
      headerName: "Supplier ID",
      width: 130,
      headerClassName: "custom-header",
      renderCell: (param) => (
        <Box sx={{ display: "flex", alignItems: "center", height: "100%" }}>
          {param.value}
        </Box>
      ),
    },
    {
      field: "supplier_name",
      headerName: "ชื่อ Supplier",
      minWidth: 200,
      flex: 1,
      headerClassName: "custom-header",
    },
    {
      field: "buyer_code_list",
      headerName: "Buyer ID",
      width: 200,
      sortable: false,
      renderCell: (param) => (
        <Box sx={{ display: "flex", alignItems: "center", height: "100%" }}>
          {param.value?.map((buyerId: string) => (
            <Chip
              key={buyerId}
              color="primary"
              label={buyerId}
              sx={{ mr: 1 }}
              size="small"
            />
          ))}
        </Box>
      ),
    },
    {
      field: "active_part",
      headerName: "Active Part",
      headerAlign: "right",
      width: 100,
      renderCell: (param) => (
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "flex-end",
            width: "100%",
            height: "100%",
          }}
        >
          <Typography
            align="right"
            color={param.value > 0 ? "green" : "red"}
            fontWeight={600}
          >
            {param.value ? param.value : 0}
          </Typography>
        </Box>
      ),
    },
    {
      field: "template_type",
      headerName: "Template Type",
      width: 130,
      headerClassName: "custom-header",
      renderCell: (param) => {
        if (param.value === "None" || !param.value) {
          return (
            <Box sx={{ display: "flex", alignItems: "center", height: "100%" }}>
              {"ไม่ได้ตั้งค่า"}
            </Box>
          );
        }
        return (
          <Box
            fontWeight={600}
            sx={{ display: "flex", alignItems: "center", height: "100%" }}
          >
            {param.value}
          </Box>
        );
      },
    },
    {
      field: "update_date",
      headerName: "Updated At",
      width: 150,
      renderCell: (param) => (
        <Box sx={{ display: "flex", alignItems: "center", height: "100%" }}>
          {formattedDateTime(param.value)}
        </Box>
      ),
    },
    {
      field: "update_by",
      headerName: "Updated By",
      width: 120,
      renderCell: (param) => (
        <Box sx={{ display: "flex", alignItems: "center", height: "100%" }}>
          {param.value}
        </Box>
      ),
    },
  ];

  return (
    <Box sx={{ width: "100%", mt: 2 }}>
      <DataGrid
        rows={data}
        columns={columns}
        loading={isLoading}
        getRowId={(row) => row.id}
        initialState={{
          pagination: {
            paginationModel: {
              pageSize: 10,
            },
          },
        }}
        pageSizeOptions={[10, 20, 50, 100]}
        disableRowSelectionOnClick
        disableColumnSelector
        disableColumnFilter
        disableColumnMenu
        sx={{
          maxHeight: "70vh",
          overflow: "auto",
        }}
      />
    </Box>
  );
};

export default FirstTierSupplierTable;
